import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { z } from 'zod';
import { isActive, uploadManager } from '@/lib/upload-manager';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  checkResultFromError,
  needsFormAndYear,
  UPLOAD_FORMATS,
  type CheckResult,
  type FormInfo,
  type NameDecisions,
  type TransmissionType,
  type Upload,
  type UploadFormat,
} from '@tabservice/tabfile-client';
import { ArrowLeft, ArrowRight, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, LabelValue, LabelValueList, SectionHeader } from '@/components/ui/card';
import { Segmented } from '@/components/ui/tabs';
import { precheckFile, type PrecheckPhase, type PrecheckResult } from '@/features/new-filing/precheck';
import { ErrorNote, FormSkeleton, Spinner, TableSkeleton } from '@/components/ui/feedback';
import { Pill } from '@/components/ui/pill';
import { Combobox } from '@/components/ui/combobox';
import { RadioCards } from '@/components/ui/toggles';
import { Table, TableWrap, TBody, TD, TH, THead, TR } from '@/components/ui/table';
import { useToast } from '@/components/ui/toast';
import { CheckIssues, CheckSummary } from '@/features/new-filing/check-report';
import { ColumnMappingEditor } from '@/features/new-filing/column-mapping-editor';
import { IrisXmlDetected, ParseErrors, Pub1220Detected } from '@/features/new-filing/detected-panels';
import { BoxTotals } from '@/features/new-filing/box-totals';
import { BlockedStep, FileSummaryStrip } from '@/features/new-filing/file-summary';
import {
  CheckingNote,
  ChosenFile,
  FileDropArea,
  FileInput,
  PassedNote,
  PrecheckIssues,
  ReviewFooter,
  ReviewParts,
  useReviewParts,
} from '@/features/new-filing/file-review';
import { PriorReferences, hasUnverifiedReferences } from '@/features/new-filing/prior-references';
import { DuplicateFileNote } from '@/features/new-filing/duplicates';
import { PasteJson } from '@/features/new-filing/paste-json';
import { newIdempotencyKey } from '@/features/new-filing/idempotency';
import { formatMeta } from '@/features/new-filing/formats';
import {
  contentTypeFor,
  fileFitsFormat,
  previewFile,
  type UploadProgress,
  type FilePreview,
} from '@/features/new-filing/file-preview';
import { UploadStatusPill } from '@/features/filings/status-pill';
import { formsQuery, meQuery, uploadQuery } from '@/lib/queries';
import { lockTaxYear, useTaxYear } from '@/lib/tax-year';
import { useSetPageTitle } from '@/components/page-title';
import { modeCopy } from '@/lib/mode';
import { tabfile, errorMessage } from '@/lib/api';
import { formatBytes, formatCount } from '@/lib/format';
import {
  NotFiledYet,
  WizardRail,
  wizardStages,
  type Stage,
  type StageTimes,
} from '@/features/new-filing/wizard-rail';

export const Route = createFileRoute('/_app/e-files/new')({
  // The shape this page takes, rather than a search box over a table for
  // every route: the screen must not rearrange itself once the data lands.
  pendingComponent: () => <FormSkeleton fields={6} />,
  // An upload started earlier (this page, another screen, or before a reload) reopens here.
  validateSearch: z.object({ uploadId: z.string().optional() }),
  loader: ({ context }) => context.queryClient.ensureQueryData(formsQuery()),
  component: NewFilingPage,
});

type Step = 'format' | 'upload' | 'checks' | 'confirm' | 'done';


/**
 * Which stage a screen is in. The upload screen covers two of them: choosing a file and
 * having it checked here is Validate, and it only becomes Upload once bytes are moving.
 * By the time the records screen is up the file is on the server, so that is Upload too.
 */
function stageOf(step: Step, transferring: boolean): Stage | 'done' {
  switch (step) {
    case 'format':
      return 'format';
    case 'upload':
      return transferring ? 'upload' : 'validate';
    case 'checks':
      return 'upload';
    case 'confirm':
      return 'submit';
    default:
      return 'done';
  }
}

function NewFilingPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const { data: formsData } = useQuery(formsQuery());
  const { data: me } = useQuery(meQuery());
  const queryClient = useQueryClient();
  // There is one tax year in the app and it lives in the header. The wizard
  // reads it and never offers a second one.
  const { taxYear } = useTaxYear();

  const search = Route.useSearch();
  const [view, setView] = useState<'file' | 'json'>('file');
  const [uploadId, setUploadIdState] = useState<string | null>(search.uploadId ?? null);
  /**
   * Which upload this page believes it is on, readable the instant it changes.
   *
   * The address is router state, which lives outside React and lands in a render before a
   * setState queued beside it has been applied. So the effect below, reading `uploadId`,
   * used to see the old value when this page navigated to a draft it had just made: its own
   * navigation looked like somebody else's, and the chosen file was thrown away a moment
   * after it was chosen. The ref is written with the state, so the comparison is
   * against what this page knows now rather than what it last rendered.
   */
  const knownUploadId = useRef<string | null>(search.uploadId ?? null);
  function setUploadId(next: string | null): void {
    knownUploadId.current = next;
    setUploadIdState(next);
  }
  // A draft opened by its address lands on the upload step until the server has said what
  // the draft holds; the effect below then moves to the step it is waiting at.
  const [step, setStep] = useState<Step>(search.uploadId ? 'upload' : 'format');
  /**
   * When each finished stage finished.
   *
   * Recorded from the stage itself rather than at each of the fifteen places the step
   * changes, so a new route through the wizard cannot forget to stamp one.
   */
  const [stageTimes, setStageTimes] = useState<StageTimes>({});
  const [format, setFormat] = useState<UploadFormat | null>(null);
  const [formType, setFormType] = useState<string | null>(null);
  const [file, setFile] = useState<File | null>(null);
  /** What reading the file here found wrong with it. Null until a check has run and failed. */
  const [precheck, setPrecheck] = useState<PrecheckResult | null>(null);
  /** The phase of the check now running, or null when none is. */
  const [checkPhase, setCheckPhase] = useState<PrecheckPhase | null>(null);
  /** What the check found on a file that passed. Stays up while the upload runs. */
  const [checkPassed, setCheckPassed] = useState<PrecheckResult | null>(null);
  const [preview, setPreview] = useState<FilePreview | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  // Transfer progress comes from the upload manager, which keeps going when this page is left.
  const tasks = useSyncExternalStore(uploadManager.subscribe, uploadManager.getSnapshot, uploadManager.getSnapshot);
  const task = tasks.find((item) => item.id === uploadId) ?? null;
  const progress: UploadProgress | null =
    task && (task.phase === 'uploading' || task.phase === 'uploaded')
      ? { loaded: task.loaded, total: task.size, percent: task.percent }
      : null;
  const [busy, setBusy] = useState(false);
  const [filingError, setFilingError] = useState<string | null>(null);
  const [blockedResult, setBlockedResult] = useState<CheckResult | null>(null);
  const [filingId, setFilingId] = useState<string | null>(null);
  const idempotencyKey = useRef(newIdempotencyKey());

  // The address is the truth about which upload this page shows. The tray's Open, the
  // Drafts list, the back button and the New e-file link all change it without leaving the
  // page, so the wizard follows it: a different upload, or none, starts the state afresh.
  // An address this page put there itself is not one of those, and is already accounted for.
  useEffect(() => {
    const next = search.uploadId ?? null;
    if (next === knownUploadId.current) return;
    setUploadId(next);
    setFile(null);
    setPreview(null);
    setPreviewError(null);
    setFilingError(null);
    setBlockedResult(null);
    setFilingId(null);
    if (!next) {
      setFormat(null);
      setFormType(null);
      setStep('format');
    } else {
      setStep('upload');
    }
  }, [search.uploadId]);

  const forms = formsData?.forms ?? [];
  const selectedForm = forms.find((item) => item.formType === formType) ?? null;
  // A form the customer cannot file for this year is a dead end, so say so
  // before the upload rather than after it.
  const formCoversYear = selectedForm ? (selectedForm.taxYears ?? []).includes(taxYear) : true;

  const upload = useQuery({
    ...uploadQuery(uploadId ?? ''),
    enabled: Boolean(uploadId),
    refetchInterval: (item) => {
      const status = item.state.data?.status;
      if (status === 'checked' || status === 'blocked' || status === 'failed' || status === 'filed') return false;
      // A draft with nothing in flight has nothing to wait for. While the tray still holds the
      // upload (transferring, just landed, or being checked), keep asking: the server notices
      // the file on the next read after the transfer ends.
      if (status === 'awaiting_file') return task && isActive(task) ? 1200 : false;
      return 1200;
    },
  });

  const uploadData = upload.data as Upload | undefined;
  // A draft the server no longer has is dropped from the tray as well.
  useEffect(() => {
    if (uploadId && upload.isError) uploadManager.dismiss(uploadId);
  }, [uploadId, upload.isError]);
  // The browser tab names the file, so two wizards side by side can be told apart.
  useSetPageTitle(uploadData ? `${uploadData.fileName || 'Draft'}, new e-file` : null);

  const stage = stageOf(step, Boolean(progress) || (task?.id === uploadId && task.phase === 'uploading'));

  // Stamp every stage the wizard has moved past, once each. A draft picked up again keeps
  // the time its first stage really happened, which is on the upload itself - a stamp of
  // "now" would tell somebody returning to yesterday's draft that they started it a moment
  // ago.
  useEffect(() => {
    const reached = wizardStages.findIndex((item) => item.id === stage);
    setStageTimes((current) => {
      let next = current;
      wizardStages.forEach((item, index) => {
        const done = stage === 'done' || reached > index;
        if (!done || next[item.id]) return;
        const at = item.id === 'format' && uploadData?.createdAt ? uploadData.createdAt : new Date().toISOString();
        next = { ...next, [item.id]: at };
      });
      return next;
    });
  }, [stage, uploadData?.createdAt]);

  // Reopened from the tray, the Drafts list or after a reload: pick the wizard up at the
  // step the upload is waiting at. Only an upload just opened, or one whose state has
  // actually moved on, decides the step. Recomputing it on every change of the format or
  // the form is what used to march the person to the next step as soon as they clicked a
  // format card, without ever pressing Continue.
  const adoptedUpload = useRef<string | null>(null);
  const adoptedStatus = useRef<string | null>(null);
  const stepNow = useRef(step);
  stepNow.current = step;

  useEffect(() => {
    if (!search.uploadId || !uploadData) return;
    const firstSight = adoptedUpload.current !== uploadData.id;
    const moved = adoptedStatus.current !== uploadData.status;
    adoptedUpload.current = uploadData.id;
    adoptedStatus.current = uploadData.status;
    if (firstSight) {
      setFormat(uploadData.format);
      setFormType(uploadData.formType ?? null);
    }
    if (!firstSight && !moved) return;
    // Somebody standing on the format step is choosing; they are not to be moved along.
    if (!firstSight && stepNow.current === 'format') return;
    const transferring = task && (task.phase === 'uploading' || task.phase === 'interrupted');
    if (uploadData.status === 'filed') {
      if (uploadData.efileId) setFilingId(uploadData.efileId);
      setStep('done');
    } else if (uploadData.status === 'awaiting_file' || transferring) {
      setStep('upload');
    } else {
      setStep('checks');
    }
  }, [search.uploadId, uploadData, task]);

  /**
   * Once the file is on Tab Service it has been checked against its draft's year, so the
   * header is held to that year until this e-file is filed or left. Changing the header
   * after that point used to leave the page saying one year while the check used another
   * (TS-8702). Before the file goes up the year is still free: startUpload makes the draft
   * match whatever the header says by then.
   */
  const transferring = task?.id === uploadId && (task.phase === 'uploading' || task.phase === 'interrupted');
  const heldYear =
    uploadData && uploadData.id === uploadId && uploadData.taxYear && uploadData.status !== 'filed' &&
    (uploadData.fileName || uploadData.status !== 'awaiting_file' || transferring)
      ? uploadData.taxYear
      : null;
  useEffect(() => {
    lockTaxYear(heldYear);
  }, [heldYear]);
  useEffect(() => () => lockTaxYear(null), []);

  // The moment a transfer this page started finishes, move on to validation.
  useEffect(() => {
    if (step === 'upload' && task && task.id === uploadId && task.phase !== 'uploading' && task.phase !== 'interrupted') {
      setStep('checks');
    }
  }, [step, task, uploadId]);
  const rowFormat = format ? needsFormAndYear(format) : false;
  const readyForFile = format !== null && (!rowFormat || formType !== null) && formCoversYear;

  async function chooseFile(next: File) {
    if (!format) return;
    setPreviewError(null);
    setPreview(null);
    setFile(next);
    // The last verdict was about the file before this one.
    setPrecheck(null);
    setCheckPassed(null);
    if (!fileFitsFormat(next, format)) {
      setPreviewError(
        `That is not a ${formatMeta[format].label} file. Expected ${formatMeta[format].extensions.join(' or ')}.`,
      );
      return;
    }
    try {
      setPreview(await previewFile(next, format));
    } catch (error) {
      setPreviewError(errorMessage(error));
      return;
    }
    // The file is one Tab Service can read, so the e-file exists from here on: it is in the
    // Drafts list while the person is still on Validate, not only once the transfer starts
    // (TS-8286). A format chosen and no file is still nothing - an empty draft would be a
    // row for an e-file that may never be started.
    void ensureDraft();
    // The check runs off the file being set, so there is nothing to start here.
  }

  /**
   * The draft row on Tab Service for what is being filed here, created once.
   *
   * Called when a file is accepted and again when the transfer begins; the second call
   * finds the draft already made. A failure is not raised at the person: the transfer
   * creates the draft too, so the only thing lost is the draft appearing early.
   */
  const draftPending = useRef(false);
  /** The year and form the draft this page made was created for, known before it is read back. */
  const draftMadeFor = useRef<{ id: string; taxYear: number; formType: string | null } | null>(null);
  async function ensureDraft(): Promise<void> {
    if (!format || uploadId || draftPending.current) return;
    if (rowFormat && !formType) return;
    draftPending.current = true;
    try {
      const created = await tabfile.POST('/v1/uploads', {
        body: { format, taxYear, ...(rowFormat && formType ? { formType } : {}) },
      });
      draftMadeFor.current = { id: created.id, taxYear, formType: rowFormat ? formType : null };
      // This page made the draft, so the first read of it must not re-decide the step.
      adoptedUpload.current = created.id;
      adoptedStatus.current = 'awaiting_file';
      setUploadId(created.id);
      void navigate({ to: '/e-files/new', search: { uploadId: created.id }, replace: true });
    } catch {
      // Left to startUpload, which creates the draft it needs and reports its own failures.
    } finally {
      draftPending.current = false;
    }
  }

  /**
   * Read the file here on the person's machine and say whether it can be filed. This is the
   * same code the server validates with for 1220 files; for the row formats it is what a
   * browser can do without the column mapping, and the server does the rest on arrival.
   */
  async function runCheck(target: File) {
    if (!format) return;
    setPrecheck(null);
    setCheckPassed(null);
    try {
      const checked = await precheckFile(target, {
        format,
        formType: rowFormat ? formType : null,
        taxYear,
        onPhase: setCheckPhase,
      });
      if (checked.ok) setCheckPassed(checked);
      else setPrecheck(checked);
    } catch (error) {
      setPreviewError(errorMessage(error));
    } finally {
      setCheckPhase(null);
    }
  }

  /**
   * The file is checked against the year it will be filed under, and that year lives in the
   * header where it can be changed at any moment. A verdict reached under a different year
   * is not one to upload on, so it is taken again.
   */
  const checkedUnder = useRef<{ file: File; taxYear: number; formType: string | null } | null>(null);
  useEffect(() => {
    if (!file || previewError || busy || progress || checkPhase) return;
    const last = checkedUnder.current;
    const now = { file, taxYear, formType: rowFormat ? formType : null };
    if (last && last.file === now.file && last.taxYear === now.taxYear && last.formType === now.formType) {
      return;
    }
    checkedUnder.current = now;
    void runCheck(file);
    // runCheck is stable for the values it closes over here, and re-running on every render
    // would check the file forever.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file, previewError, taxYear, formType, rowFormat]);

  /**
   * The format, form and year become a draft upload the moment they are chosen, so the
   * e-file exists on Tab Service from the first step and can be resumed from anywhere.
   */
  async function startDraft() {
    if (!format) return;
    // Nothing is created here on purpose. A format chosen is not an e-file started: making a
    // draft at this point leaves a row, a tray entry and a resumable "e-file" for a file that
    // may never be chosen. The draft is created when a file has been read and accepted, in
    // startUpload.
    if (!uploadData) {
      setStep('upload');
      return;
    }
    setBusy(true);
    setFilingError(null);
    try {
      const wantedFormType = rowFormat && formType ? formType : null;
      // Continue pressed on a draft that already says exactly this: carry on with it.
      if (
        uploadData &&
        !uploadData.fileName &&
        uploadData.format === format &&
        (uploadData.taxYear ?? null) === taxYear &&
        (uploadData.formType ?? null) === wantedFormType
      ) {
        setStep('upload');
        return;
      }
      // The choice changed before any file was chosen, so the draft describes the wrong
      // thing. An empty draft is worth nothing; it goes, and this choice becomes the draft.
      if (uploadData && !uploadData.fileName && uploadData.status === 'awaiting_file') {
        await tabfile
          .DELETE('/v1/uploads/{uploadId}', { params: { path: { uploadId: uploadData.id } } })
          .catch(() => undefined);
        uploadManager.dismiss(uploadData.id);
      }
      const created = await tabfile.POST('/v1/uploads', {
        body: {
          format,
          // The year goes up for every format. A 1220 file or IRIS XML declares
          // its own, and Tab Service blocks the upload when the two disagree.
          taxYear,
          ...(rowFormat && formType ? { formType } : {}),
        },
      });
      // This page made the draft, so it already knows it: the first read of it must not
      // re-decide the step under somebody who has since pressed Back.
      adoptedUpload.current = created.id;
      adoptedStatus.current = 'awaiting_file';
      setUploadId(created.id);
      setStep('upload');
      void navigate({ to: '/e-files/new', search: { uploadId: created.id }, replace: true });
    } catch (error) {
      setFilingError(errorMessage(error));
    } finally {
      setBusy(false);
    }
  }

  /** `nameDecisions`: how Resolve Names settled the recipient names; the file is read with them. */
  async function startUpload(nameDecisions?: NameDecisions) {
    if (!file || !format || busy) return;
    // A draft that already carries this file is not uploaded again.
    if (task && task.id === uploadId && task.phase === 'uploading') return;
    if (uploadData && uploadData.status !== 'awaiting_file') return;
    // The file was checked when it was chosen, and the button that gets here is not offered
    // until that passed. This is the transfer, and nothing else.
    if (!checkPassed || checkPhase) return;
    setBusy(true);
    setFilingError(null);
    try {
      // The draft learns its file here and hands back the link to put it at.
      let draftId = uploadId;
      // The draft was made when the file was chosen, under the year the header said then. A
      // year changed since (the check's own advice when the rows are for another year) left
      // the file checked for one year and uploaded into a draft for another, which the server
      // then blocked as "does not match the selected tax year". A draft with no file yet is
      // worth nothing, so it goes and one for the current choice takes its place.
      const made =
        uploadData && uploadData.id === draftId
          ? {
              taxYear: uploadData.taxYear ?? null,
              formType: uploadData.formType ?? null,
              empty: !uploadData.fileName && uploadData.status === 'awaiting_file',
            }
          : draftMadeFor.current && draftMadeFor.current.id === draftId
            ? { ...draftMadeFor.current, empty: true }
            : null;
      const wantedFormType = rowFormat ? formType : null;
      if (draftId && made && made.empty && (made.taxYear !== taxYear || made.formType !== wantedFormType)) {
        const stale = draftId;
        await tabfile
          .DELETE('/v1/uploads/{uploadId}', { params: { path: { uploadId: stale } } })
          .catch(() => undefined);
        uploadManager.dismiss(stale);
        draftId = null;
      }
      if (!draftId) {
        // Normally the draft was made when the file was accepted (TS-8286); this covers a
        // file chosen before that could finish, and a draft creation that failed then.
        const created = await tabfile.POST('/v1/uploads', {
          body: { format, taxYear, ...(rowFormat && formType ? { formType } : {}) },
        });
        draftId = created.id;
        adoptedUpload.current = created.id;
        adoptedStatus.current = 'awaiting_file';
        setUploadId(created.id);
      }
      const link = await tabfile.POST('/v1/uploads/{uploadId}/link', {
        params: { path: { uploadId: draftId } },
        body: {
          fileName: file.name,
          contentType: contentTypeFor(format),
          sizeBytes: file.size,
          ...(nameDecisions ? { nameDecisions } : {}),
        },
      });
      // From here the transfer belongs to the upload manager: leaving this page does not stop
      // it, the header tray shows it, and the wizard moves on when it is done.
      uploadManager.start({
        uploadId: draftId,
        uploadUrl: link.uploadUrl,
        file,
        format,
        formType: rowFormat ? formType : null,
        taxYear,
      });
      void navigate({ to: '/e-files/new', search: { uploadId: draftId }, replace: true });
    } catch (error) {
      setFilingError(errorMessage(error));
    } finally {
      setCheckPhase(null);
      setBusy(false);
    }
  }

  async function createFiling() {
    if (!uploadId) return;
    setBusy(true);
    setFilingError(null);
    setBlockedResult(null);
    try {
      const filing = await tabfile.POST('/v1/e-files', {
        body: { uploadId },
        idempotencyKey: idempotencyKey.current,
      });
      setFilingId(filing.id);
      setStep('done');
      // The draft is an e-file now. The drafts list and the e-file list are cached for 30
      // seconds, so going straight back to Drafts still showed it as a draft (TS-8702).
      void queryClient.invalidateQueries({ queryKey: ['uploads'] });
      void queryClient.invalidateQueries({ queryKey: ['e-files'] });
      toast.success('E-file created', `${formatCount(filing.recordCount)} records queued for the IRS.`);
    } catch (error) {
      const blocked = checkResultFromError(error);
      if (blocked) {
        setBlockedResult(blocked);
        setFilingError('Nothing was filed. Some records are blocked.');
      } else {
        setFilingError(errorMessage(error));
      }
    } finally {
      setBusy(false);
    }
  }

  /**
   * Back from the records screen, to file something else. The draft that already holds the
   * previous file is let go of here - it keeps its file and stays resumable under Drafts -
   * and so is the file itself, so the only way forward is to choose another one. Leaving the
   * file selected is what let one file be uploaded again on every visit to this screen.
   */
  function chooseAnotherFile() {
    void navigate({ to: '/e-files/new', search: {}, replace: true });
    adoptedUpload.current = null;
    adoptedStatus.current = null;
    // Deliberately the plain setter: letting go of the draft is exactly the case the effect
    // above is for, so the address losing its uploadId must still start the page afresh.
    setUploadIdState(null);
    setFile(null);
    setPreview(null);
    setPreviewError(null);
    setPrecheck(null);
    setCheckPassed(null);
    setCheckPhase(null);
    setFilingError(null);
    setStep('upload');
  }

  /** Let go of the chosen file and its verdict, staying on this step for another one. */
  function clearFile() {
    setFile(null);
    setPreview(null);
    setPreviewError(null);
    setPrecheck(null);
    setCheckPassed(null);
    setCheckPhase(null);
    setFilingError(null);
  }

  function reset() {
    setStep('format');
    void navigate({ to: '/e-files/new', search: {}, replace: true });
    setFile(null);
    setPreview(null);
    setPreviewError(null);
    setPrecheck(null);
    setCheckPassed(null);
    setCheckPhase(null);
    // As in chooseAnotherFile: the effect above is what clears the format and the form, so
    // the address change must reach it.
    setUploadIdState(null);
    setFilingId(null);
    setFilingError(null);
    setBlockedResult(null);
    idempotencyKey.current = newIdempotencyKey();
  }

  return (
    <>
      {/*
        One row: back on the left, the choice of how you are sending records on the right, and
        the sentence explaining that choice directly under it. It used to be three stacked
        bands - a link, a control, and a full-width paragraph - which pushed the step itself
        most of the way down the screen before anything could be done on it.
      */}
      <div className="flex flex-col items-start gap-3">
        <Button asChild variant="ghost" size="sm" className="-ml-3 shrink-0">
          <Link to="/e-files">
            <ArrowLeft className="h-4 w-4" aria-hidden="true" />
            All e-files
          </Link>
        </Button>
        {/*
          On the left, under the back link, where the page is read from. Pinned to the right
          it sat across a gap from everything it governs, and on a wide screen the sentence
          under it trailed off the far edge.
        */}
        <div className="flex min-w-0 flex-col items-start gap-2" data-tour="send-mode">
          <Segmented
            ariaLabel="How you are sending records"
            value={view}
            items={[
              { value: 'file', label: 'Upload a file' },
              { value: 'json', label: 'Paste JSON' },
            ]}
            onValueChange={(next) => setView(next as 'file' | 'json')}
          />
          {/*
            One line. What each format is for is written on the format cards themselves,
            where somebody is actually choosing between them.
          */}
          <p className="max-w-[60ch] text-sm text-muted-foreground">
            {view === 'json'
              ? 'Up to 1,000 canonical records, checked exactly as a file is.'
              : 'The usual route: CSV, Excel, an IRS 1220 file or IRIS XML.'}
          </p>
        </div>
      </div>

      {view === 'file' ? (
        // Content on the left, the rail on the right, the way the portal lays out its own
        // upload. Below the rail's breakpoint the rail goes first: on a narrow screen what is
        // being filed and how far along it is belong above the step, not below it. The rail
        // stays put on every step; a wide table such as Resolve Names scrolls in its own box.
        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_320px] lg:items-start">
          <div className="order-2 flex min-w-0 flex-col gap-6 lg:order-1">
          <NotFiledYet hasDraft={Boolean(uploadData)} />

          {search.uploadId && upload.isError ? (
            <Card>
              <ErrorNote
                title="This draft no longer exists"
                detail="It was discarded, or it belongs to a different account. Start a new e-file or open one from the Drafts list."
                action={
                  <div className="flex flex-wrap gap-2">
                    <Button variant="primary" size="sm" onClick={reset}>
                      New e-file
                    </Button>
                    <Button asChild variant="outline" size="sm">
                      <Link to="/e-files">All e-files</Link>
                    </Button>
                  </div>
                }
              />
            </Card>
          ) : step === 'format' ? (
            <FormatStep
              forms={forms}
              format={format}
              formType={formType}
              taxYear={taxYear}
              formCoversYear={formCoversYear}
              ready={readyForFile}
              onFormat={(value) => {
                setFormat(value);
                setFile(null);
                setPreview(null);
                setPreviewError(null);
                if (!needsFormAndYear(value)) setFormType(null);
              }}
              onFormType={setFormType}
              onNext={() => void startDraft()}
            />
          ) : null}

          {step === 'upload' && format ? (
            <UploadStep
              format={format}
              file={file}
              preview={preview}
              previewError={previewError}
              progress={progress}
              busy={busy || (task?.phase === 'uploading' && task.id === uploadId)}
              error={filingError ?? (task?.id === uploadId && task.phase === 'interrupted' ? task.error : null)}
              precheck={precheck}
              checkPhase={checkPhase}
              checkPassed={checkPassed}
              onChoose={(next) => void chooseFile(next)}
              onClear={clearFile}
              onBack={() => setStep('format')}
              onUpload={(nameDecisions) => void startUpload(nameDecisions)}
            />
          ) : null}

          {step === 'checks' && format ? (
            <ChecksStep
              format={format}
              upload={uploadData}
              taxYear={taxYear}
              loading={upload.isPending}
              onBack={chooseAnotherFile}
              onNext={() => setStep('confirm')}
              onBackground={() => void navigate({ to: '/e-files' })}
            />
          ) : null}

          {step === 'confirm' && format ? (
            <ConfirmStep
              format={format}
              formType={uploadData?.formType ?? uploadData?.detected?.formType ?? formType ?? 'read from the file'}
              taxYear={taxYear}
              fileName={file?.name ?? uploadData?.fileName ?? 'your file'}
              recordCount={uploadData?.recordCount ?? 0}
              mode={me?.mode ?? 'ats'}
              busy={busy}
              error={filingError}
              blocked={blockedResult}
              onBack={() => setStep('checks')}
              onFile={() => void createFiling()}
            />
          ) : null}

          {step === 'done' && filingId ? (
            <DoneStep
              filingId={filingId}
              recordCount={uploadData?.recordCount ?? 0}
              onAnother={reset}
              onOpen={() => void navigate({ to: '/e-files/$efileId', params: { efileId: filingId } })}
            />
          ) : null}
          </div>

          <div className="order-1 lg:order-2 lg:sticky lg:top-6">
            <WizardRail
              current={stage}
              times={stageTimes}
              formType={uploadData?.formType ?? uploadData?.detected?.formType ?? formType}
              formatLabel={format ? (formatMeta[format]?.label ?? format) : null}
              upload={uploadData ?? null}
              sizeBytes={task?.size ?? file?.size ?? null}
              fileName={file?.name ?? task?.fileName ?? null}
            />
          </div>
        </div>
      ) : (
        <JsonView
          forms={forms}
          taxYear={taxYear}
          onFiled={(id) => {
            toast.success('E-file created', 'The records are queued for the IRS.');
            void navigate({ to: '/e-files/$efileId', params: { efileId: id } });
          }}
        />
      )}
    </>
  );
}

function FormatStep({
  forms,
  format,
  formType,
  taxYear,
  formCoversYear,
  ready,
  onFormat,
  onFormType,
  onNext,
}: {
  forms: FormInfo[];
  format: UploadFormat | null;
  formType: string | null;
  taxYear: number;
  formCoversYear: boolean;
  ready: boolean;
  onFormat: (value: UploadFormat) => void;
  onFormType: (value: string) => void;
  onNext: () => void;
}) {
  const rowFormat = format ? needsFormAndYear(format) : false;

  return (
    <Card>
      <SectionHeader
        title="File format"
        description="Select the format of the file you are submitting."
      />

      <div data-tour="format-cards">
        <RadioCards
          ariaLabel="File format"
          value={format}
          onValueChange={(value) => onFormat(value as UploadFormat)}
          options={UPLOAD_FORMATS.map((value) => ({
            value,
            title: formatMeta[value].label,
            description: formatMeta[value].description,
          }))}
        />
      </div>

      <div data-tour="form-year" className="pt-6">
        {!format ? (
          <p className="text-base text-muted-foreground">
            Select a format above. A CSV or an Excel file needs a form chosen here; a 1220 file or IRIS XML carries
            its own.
          </p>
        ) : rowFormat ? (
          <>
            <p className="text-base font-medium text-gray-3">Form</p>
            <p className="mt-1 text-base text-muted-foreground">
              Row and column files do not identify their form, so select it here. Forms this account is not enabled
              for are listed but cannot be selected.
            </p>
            <div className="mt-4 max-w-sm">
              <Combobox
                ariaLabel="Form type"
                value={formType ?? undefined}
                placeholder="Choose a form"
                onValueChange={onFormType}
                options={forms.map((item) => ({
                  value: item.formType,
                  label: item.enabled ? item.formType : `${item.formType} (not enabled)`,
                  disabled: !item.enabled,
                }))}
              />
            </div>
            <TaxYearNote taxYear={taxYear} />
            {formType && !formCoversYear ? (
              <p className="mt-2 text-base text-destructive">
                {formType} is not available for tax year {taxYear}. Change the tax year in the header to submit it.
              </p>
            ) : null}
          </>
        ) : (
          <>
            <p className="text-base text-muted-foreground">
              The form type is read from the file, so there is nothing to select here.
            </p>
            <TaxYearNote taxYear={taxYear} />
          </>
        )}
      </div>

      <div className="flex justify-end pt-6">
        <Button variant="primary" disabled={!ready} onClick={onNext}>
          Continue
          <ArrowRight className="h-4 w-4" aria-hidden="true" />
        </Button>
      </div>
    </Card>
  );
}

/** The one place the wizard mentions the year. It is not a control. */
function TaxYearNote({ taxYear }: { taxYear: number }) {
  return (
    <p className="mt-2 text-base text-muted-foreground">
      E-file for tax year {taxYear}. Change it from the header if needed.
    </p>
  );
}

function UploadStep({
  format,
  file,
  preview,
  previewError,
  progress,
  busy,
  error,
  precheck,
  checkPhase,
  checkPassed,
  onChoose,
  onClear,
  onBack,
  onUpload,
}: {
  format: UploadFormat;
  file: File | null;
  preview: FilePreview | null;
  previewError: string | null;
  progress: UploadProgress | null;
  busy: boolean;
  error: string | null;
  precheck: PrecheckResult | null;
  checkPhase: PrecheckPhase | null;
  checkPassed: PrecheckResult | null;
  onChoose: (file: File) => void;
  onClear: () => void;
  onBack: () => void;
  onUpload: (nameDecisions?: NameDecisions) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const meta = formatMeta[format];
  /** Bytes are moving: the check is behind us and this screen is now the upload. */
  const transferring = Boolean(progress) || (busy && Boolean(checkPassed) && !checkPhase);
  /** The verdict on the chosen file, passed or not: what was mapped and what it adds up to. */
  const review = checkPassed ?? precheck;
  const state = useReviewParts(file, review);
  const canUpload = Boolean(file) && !busy && !previewError && !checkPhase && Boolean(checkPassed) && state.reviewed;

  return (
    <>
      <Card>
        <SectionHeader
          // One screen covers two stages, so it says which one it is on rather than sitting
          // under "Validate" while the file is being sent.
          title={transferring ? 'Upload' : 'Validate'}
          description={
            transferring
              ? 'The file passed the checks on your machine. It is being sent to Tab Service now.'
              : file
                ? 'The file is read and checked here on your machine. Review how it was read, then upload it.'
                : 'Choose a file and it is read and checked here on your machine. Nothing is uploaded until you have reviewed it.'
          }
          actions={<Pill tone="info">{meta.label}</Pill>}
        />

        {file ? (
          <>
            <FileInput accept={meta.accept} onChoose={onChoose} inputRef={inputRef} />
            <ChosenFile
              file={file}
              progress={progress}
              busy={busy}
              onChange={() => inputRef.current?.click()}
              onRemove={onClear}
            />
          </>
        ) : (
          <FileDropArea
            accept={meta.accept}
            hint={`${meta.extensions.join(' or ')}, up to 100 MB per transmission`}
            onChoose={onChoose}
            inputRef={inputRef}
          />
        )}

        {previewError ? (
          <div className="mt-5">
            <ErrorNote title="Could not read that file" detail={previewError} />
          </div>
        ) : null}
        {error ? (
          <div className="mt-5">
            <ErrorNote title="Upload did not finish" detail={error} />
          </div>
        ) : null}

        {file && checkPhase ? (
          <div className="mt-5">
            <CheckingNote phase={checkPhase} />
          </div>
        ) : null}

        {file && checkPassed && !checkPhase ? (
          <div className="mt-5">
            <PassedNote result={checkPassed} uploading={busy || Boolean(progress)} />
          </div>
        ) : null}

        {/*
          A file with no review parts (an IRIS transmission, or a file that could not be
          read far enough to map) says what was found here, since there is no part to show it in.
        */}
        {file && precheck && !precheck.ok && state.parts.length === 0 ? (
          <div className="mt-5">
            <PrecheckIssues result={precheck} />
          </div>
        ) : null}

        {file && review && !checkPhase && !transferring ? (
          <div className="mt-5">
            <ReviewParts file={file} review={review} state={state} />
          </div>
        ) : null}

        <ReviewFooter
          state={state}
          transferring={transferring}
          canUpload={canUpload}
          busy={busy}
          phase={checkPhase}
          onBack={onBack}
          onUpload={() => onUpload(state.names.forUpload())}
          uploadLabel={
            busy && progress
              ? progress.percent < 100
                ? `Uploading ${progress.percent}%`
                : 'Finishing'
              : busy
                ? 'Starting the upload'
                : 'Upload'
          }
        />
      </Card>

      {/* The first rows, alongside the mapping they were read with; for a file with no mapping, on their own. */}
      {preview && !transferring && (state.part === 'mapping' || state.parts.length === 0) ? (
        <PreviewCard preview={preview} />
      ) : null}
    </>
  );
}


function PreviewCard({ preview }: { preview: FilePreview }) {
  if (preview.kind === 'table') {
    return (
      <Card>
        <SectionHeader
          title="First rows"
          description={`${preview.columns.length} columns${preview.truncated ? ', more rows follow in the file' : ''}`}
        />
        <TableWrap className="max-h-[360px] overflow-y-auto">
          <Table>
            <THead>
              <tr>
                {preview.columns.map((column, index) => (
                  <TH key={`${column}-${index}`}>{column || `Column ${index + 1}`}</TH>
                ))}
              </tr>
            </THead>
            <TBody>
              {preview.rows.map((row, rowIndex) => (
                <TR key={rowIndex}>
                  {preview.columns.map((_, columnIndex) => (
                    <TD key={columnIndex} className="whitespace-nowrap text-muted-foreground">
                      {row[columnIndex] ?? ''}
                    </TD>
                  ))}
                </TR>
              ))}
            </TBody>
          </Table>
        </TableWrap>
      </Card>
    );
  }

  if (preview.kind === 'text') {
    return (
      <Card>
        <SectionHeader
          title="First lines"
          description={preview.truncated ? 'The file continues past these lines.' : 'That is the whole file.'}
        />
        <pre className="overflow-x-auto rounded-lg bg-blue-bglayout px-4 py-3 font-mono text-sm text-gray-3">
          {preview.lines.map((line, index) => (
            <span key={index} className="block whitespace-pre">
              {line}
            </span>
          ))}
        </pre>
      </Card>
    );
  }

  return (
    <Card>
      <SectionHeader title="File details" description="The rest is read once the file arrives." />
      <LabelValueList>
        <LabelValue label="Root element">{preview.rootElement ?? 'not found'}</LabelValue>
        <LabelValue label="File size">{formatBytes(preview.sizeBytes)}</LabelValue>
      </LabelValueList>
    </Card>
  );
}

function blockedGuidance(format: UploadFormat, upload: Upload | undefined): string {
  if (format === 'irisxml') {
    return 'Fix the schema errors listed below and upload the file again. Nothing has been sent to the IRS.';
  }
  if (format === 'pub1220') {
    const formTypes = upload?.detected?.formTypes ?? [];
    if (formTypes.length > 1) {
      return 'This file holds more than one form type. Split it into one file per form type and upload them separately.';
    }
    return 'Fix the lines listed below and upload the 1220 file again. Nothing has been sent to the IRS.';
  }
  return 'Fix the rows listed below and upload the source file again. Nothing has been sent to the IRS.';
}

function ChecksStep({
  format,
  upload,
  taxYear,
  loading,
  onBack,
  onNext,
  onBackground,
}: {
  format: UploadFormat;
  upload: Upload | undefined;
  taxYear: number;
  loading: boolean;
  onBack: () => void;
  onNext: () => void;
  /** Leave the page while the file is being checked; the uploads tray follows it. */
  onBackground: () => void;
}) {
  const status = upload?.status;
  const working = !status || status === 'awaiting_file' || status === 'received' || status === 'parsing';
  const failed = status === 'failed';
  const rowFormat = needsFormAndYear(format);

  // A 1220 file or an IRIS transmission declares its own year. If it disagrees
  // with the header, the e-file would land in the wrong year, so it is blocked.
  const fileYear = rowFormat ? null : (upload?.detected?.taxYear ?? null);
  const yearMismatch = fileYear !== null && fileYear !== taxYear;

  // An IRIS correction or replacement names the returns it amends. Tab Service can
  // only file it if every one of those references is a return it sent.
  const transmissionType: TransmissionType | null = upload?.detected?.transmissionType ?? null;
  const amends = format === 'irisxml' && (transmissionType === 'correction' || transmissionType === 'replacement');
  const unverifiedRefs = amends && hasUnverifiedReferences(upload?.detected);
  const duplicateOf = upload?.duplicateOfEfileId ?? null;

  const blocked = status === 'blocked' || yearMismatch || unverifiedRefs || Boolean(duplicateOf);

  return (
    <>
      <Card>
        <SectionHeader
          title="Records"
          description={
            rowFormat
              ? 'The file is on Tab Service. Every row is read, the columns are matched, and the IRS rules are applied again.'
              : 'The file is on Tab Service. It is read as supplied and the IRS rules are applied again before submission.'
          }
          actions={status ? <UploadStatusPill status={status} /> : null}
        />

        {working ? (
          <div className="flex flex-col gap-4 py-4">
            <div className="flex items-center gap-3">
              <Spinner />
              <p className="text-base text-muted-foreground">
                {status === 'parsing'
                  ? 'File received. Every record is being read and checked against the IRS rules. A file of 50,000 records takes about a minute.'
                  : status === 'received'
                    ? 'File received. The check starts in a few seconds.'
                    : 'Confirming the file arrived in storage.'}
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <Button variant="outline" onClick={onBackground}>
                Continue in the background
              </Button>
              <p className="text-base text-muted-foreground">
                You do not have to wait here. The uploads icon in the header shows the check running and tells you
                when it is done; open it from there to pick up at this step.
              </p>
            </div>
          </div>
        ) : loading ? (
          <TableSkeleton rows={4} columns={4} />
        ) : failed ? (
          <BlockedStep
            title="Could not read that file"
            detail={`Confirm the file is a ${formatMeta[format].label} file, then choose it again.`}
          />
        ) : (
          <div className="flex flex-col gap-4">
            {/*
              What was read, as five labelled figures rather than as a sentence. These are
              what somebody checks against their own system before letting anything go to the
              IRS, and a sentence is the form they get skimmed in.
            */}
            <FileSummaryStrip
              fileName={upload?.fileName ?? 'Your file'}
              startedAt={upload?.createdAt ?? null}
              total={upload?.recordCount ?? 0}
              succeeded={Math.max((upload?.recordCount ?? 0) - (upload?.check?.blocked ?? 0), 0)}
              failed={upload?.check?.blocked ?? 0}
            />
            {upload?.check ? (
              <div className="flex flex-wrap items-center justify-end gap-3">
                <CheckSummary result={upload.check} />
              </div>
            ) : null}
            {/*
              A wizard that cannot go on says so across the middle of the step, not in a note
              between two panels. Somebody who scrolls past a note spends the next minute
              looking for a Continue button that is never going to enable.
            */}
            {duplicateOf ? (
              <DuplicateFileNote filingId={duplicateOf} />
            ) : yearMismatch ? (
              <BlockedStep
                title="This file is for a different tax year"
                detail={`It holds tax year ${fileYear} and this account is working in ${taxYear}. Switch the tax year in the header to ${fileYear} to submit it, or choose a different file.`}
              />
            ) : unverifiedRefs ? (
              <BlockedStep
                title="This file references an e-file Tab Service did not send"
                detail="Every prior reference in a correction or a replacement has to match a return this account filed through Tab Service. The references are listed below, with the ones that could not be matched."
              />
            ) : blocked ? (
              <BlockedStep
                title="This file cannot be submitted as it is"
                detail={
                  <>
                    {blockedGuidance(format, upload)}
                    {rowFormat ? (
                      <>
                        {' '}
                        If the form is not enabled for this account, ask Tab Service to enable it and upload again.
                      </>
                    ) : null}
                  </>
                }
              />
            ) : null}
          </div>
        )}

        <div className="flex items-center justify-between gap-3 pt-6">
          <Button variant="ghost" onClick={onBack}>
            <ArrowLeft className="h-4 w-4" aria-hidden="true" />
            Choose a different file
          </Button>
          <Button variant="primary" disabled={working || blocked || failed} onClick={onNext}>
            Continue
            <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </Button>
        </div>
      </Card>

      {!working && upload && format === 'pub1220' ? <Pub1220Detected upload={upload} /> : null}
      {!working && upload && format === 'irisxml' ? <IrisXmlDetected upload={upload} /> : null}

      {!working && !failed && upload ? (
        <BoxTotals
          boxes={upload.detected?.boxTotals}
          unmappedColumns={upload.unmappedColumns ?? []}
          recordCount={upload.recordCount ?? 0}
          description={`What Tab Service read from the ${formatCount(upload.recordCount ?? 0)} records on arrival, box by box. These should match what you saw before the upload.`}
        />
      ) : null}

      {!working && upload?.detected && amends && transmissionType ? (
        <PriorReferences detected={upload.detected} transmissionType={transmissionType} />
      ) : null}

      {rowFormat && upload && (upload.columnMapping || upload.unmappedColumns?.length) ? (
        <ColumnMappingEditor
          uploadId={upload.id}
          formType={upload.formType ?? undefined}
          mapping={upload.columnMapping ?? {}}
          unmapped={upload.unmappedColumns ?? []}
          overrides={upload.columnMappingOverrides ?? undefined}
          // Once it has been filed the mapping describes what was sent, and changing it
          // would describe a file that no longer decides anything.
          editable={upload.status !== 'filed'}
        />
      ) : null}

      {upload?.parseErrors?.length ? <ParseErrors errors={upload.parseErrors} /> : null}

      {upload?.check ? (
        <Card>
          <SectionHeader
            title="Records with problems"
            description={rowFormat ? 'Grouped by issue, with the row numbers from the file.' : 'Grouped by issue.'}
          />
          <CheckIssues issues={upload.check.issues} recordCount={upload.recordCount ?? undefined} />
        </Card>
      ) : null}
    </>
  );
}

function ConfirmStep({
  format,
  formType,
  taxYear,
  fileName,
  recordCount,
  mode,
  busy,
  error,
  blocked,
  onBack,
  onFile,
}: {
  format: UploadFormat;
  formType: string;
  taxYear: number | null;
  fileName: string;
  recordCount: number;
  mode: 'ats' | 'live';
  busy: boolean;
  error: string | null;
  blocked: CheckResult | null;
  onBack: () => void;
  onFile: () => void;
}) {
  return (
    <>
      <Card>
        <SectionHeader
          title="Review and submit"
          description={
            mode === 'live'
              ? 'This account is live. This submission is a real IRS e-file.'
              : 'This account is in IRS test mode. E-files go to the IRS test system, not the live IRS.'
          }
          actions={<Pill tone={modeCopy[mode].tone}>{modeCopy[mode].label} mode</Pill>}
        />
        <LabelValueList>
          <LabelValue label="Sent as">{formatMeta[format].label}</LabelValue>
          <LabelValue label="Form">{formType}</LabelValue>
          <LabelValue label="Tax year">{taxYear ?? 'read from the file'}</LabelValue>
          <LabelValue label="File">{fileName}</LabelValue>
          <LabelValue label="Records ready">{formatCount(recordCount)}</LabelValue>
        </LabelValueList>
        {format === 'irisxml' ? (
          <p className="pt-4 text-base text-muted-foreground">
            The XML is sent as supplied, under Tab Service&apos;s transmitter header.
          </p>
        ) : null}
        <div className="flex items-center justify-between gap-3 pt-6">
          <Button variant="ghost" onClick={onBack}>
            <ArrowLeft className="h-4 w-4" aria-hidden="true" />
            Back to validation
          </Button>
          <Button variant="primary" disabled={busy} onClick={onFile}>
            {busy ? <Spinner /> : <Send className="h-4 w-4" aria-hidden="true" />}
            Submit to the IRS
          </Button>
        </div>
      </Card>

      {error ? (
        <Card>
          <ErrorNote title="Nothing was filed" detail={error} />
        </Card>
      ) : null}

      {blocked ? (
        <Card>
          <SectionHeader title="Blocked records" actions={<CheckSummary result={blocked} />} />
          <CheckIssues issues={blocked.issues} />
        </Card>
      ) : null}
    </>
  );
}

function JsonView({
  forms,
  taxYear,
  onFiled,
}: {
  forms: FormInfo[];
  taxYear: number;
  onFiled: (filingId: string) => void;
}) {
  const [formType, setFormType] = useState<string | null>(null);
  const selectedForm = forms.find((item) => item.formType === formType) ?? null;
  const formCoversYear = selectedForm ? (selectedForm.taxYears ?? []).includes(taxYear) : true;

  return (
    <>
      <Card>
        <SectionHeader title="Form" description="Pasted records are canonical rows. Select the form they belong to." />
        <div className="max-w-sm">
          <Combobox
            ariaLabel="Form type"
            value={formType ?? undefined}
            placeholder="Choose a form"
            onValueChange={setFormType}
            options={forms.map((item) => ({
              value: item.formType,
              label: item.enabled ? item.formType : `${item.formType} (not enabled)`,
              disabled: !item.enabled,
            }))}
          />
        </div>
        <TaxYearNote taxYear={taxYear} />
        {formType && !formCoversYear ? (
          <p className="mt-2 text-base text-destructive">
            {formType} is not available for tax year {taxYear}. Change the tax year in the header to submit it.
          </p>
        ) : null}
      </Card>

      {formType && formCoversYear ? (
        <PasteJson formType={formType} taxYear={taxYear} onFiled={onFiled} />
      ) : (
        <Card>
          <p className="text-base text-muted-foreground">Select a form above to paste records.</p>
        </Card>
      )}
    </>
  );
}

function DoneStep({
  filingId,
  recordCount,
  onAnother,
  onOpen,
}: {
  filingId: string;
  recordCount: number;
  onAnother: () => void;
  onOpen: () => void;
}) {
  return (
    <Card>
      <SectionHeader
        title="E-file submitted"
        description={`${formatCount(recordCount)} records queued. Transmissions and the IRS response appear on the e-file page.`}
      />
      <p className="text-base text-muted-foreground">{filingId}</p>
      <div className="flex flex-wrap gap-3 pt-6">
        <Button variant="primary" onClick={onOpen}>
          Open the e-file
          <ArrowRight className="h-4 w-4" aria-hidden="true" />
        </Button>
        <Button variant="outline" onClick={onAnother}>
          Start another e-file
        </Button>
      </div>
    </Card>
  );
}
